import {
  LocalAudioTrack,
  LocalDataTrack,
  LocalVideoTrack,
  RemoteAudioTrack,
  RemoteParticipant,
  RemoteVideoTrack,
  Room,
  connect,
  Logger,
  runPreflight,
  isSupported,
} from 'twilio-video';
import { SbMediaStreamType, SbMediaStreams } from '../media-streams';
import { SbLoggerI } from '../types';
import {
  ConnectOptions,
  SbPeerDisconnectCode,
  WebRtcClient,
  WebRtcClientEvent,
} from './client';

type RemoteAVTrack = RemoteAudioTrack | RemoteVideoTrack;
type LocalAVTrack = LocalAudioTrack | LocalVideoTrack;

const logger = Logger.getLogger('twilio-video');
let log: SbLoggerI | null = null;

// overwrite the loglevel module's original methodFactory
//@ts-ignore
logger.methodFactory = function (methodName, logLevel, loggerName) {
  //@ts-ignore
  return function (datetime, logLevel, component, message, data) {
    if (!log) {
      return;
    }

    const skip: string[] = ['{"type":"heartbeat"}'];
    if (skip.find(s => message.includes(s))) {
      return;
    }
    if (message === 'event' && data && data.group === 'quality') {
      return;
    }

    //@ts-ignore
    const logMethod = log[methodName];
    if (logMethod && !data) {
      logMethod(`[twilio-video] ${message}`);
    }
  };
};

logger.setLevel('debug');

export class TwilioVideoWebRtcClient extends WebRtcClient {
  #localDataTrack: null | LocalDataTrack = null;
  #roomName: null | string = null;
  #room: null | Room = null;
  #streamsIn = {
    [SbMediaStreamType.camera]: null,
    [SbMediaStreamType.display]: null,
  } as Record<SbMediaStreamType, null | MediaStream>;
  #streamsOut = {
    [SbMediaStreamType.camera]: null,
    [SbMediaStreamType.display]: null,
  } as Record<
    SbMediaStreamType,
    null | { stream: MediaStream; twilioTracks: LocalAVTrack[] }
  >;
  #streamToAvTrack: Record<string, LocalAVTrack[]> = {};
  #asyncTrackOperation: null | Promise<unknown> = null;
  #isDataChannelOpen: boolean = false;

  constructor(mediaStreams: SbMediaStreams, logger: SbLoggerI) {
    super(mediaStreams, logger);

    this.logger = logger;
    log = logger;
    this.mediaStreams = mediaStreams;
  }

  connect(options: ConnectOptions) {
    if (!isSupported) {
      this.logger.warn('Unsupported browser - issues expected');
    }

    if (!options.token || !options.room) {
      this.logger.warn(
        `Called TwilioVideoWebRtcClient.connect with invalid options ${options}`,
      );
      return;
    }

    const roomName = options.room;
    if (this.#room) {
      if (this.#room.name !== roomName) {
        this.logger.warn(
          `Requested connection to room ${roomName} while still connected to room ${
            this.#room.name
          }. Disconnecting from the old room.`,
        );
        this.disconnect();
      } else if (this.#room.state === 'disconnected') {
        this.logger.warn(
          `Requested a new connection to disconnected room ${roomName}. Cleaning up.`,
        );
        this.disconnect();
      } else {
        this.logger.info(
          `Room ${roomName} is already active with state: ${
            this.#room.state
          }. Nothing to do.`,
        );
        return;
      }
    }

    this.#roomName = options.room;

    // In case of error, give up retries after 1s.
    // App-level retries take it from there, and we don't want
    // to interfere.
    this.#localDataTrack = new LocalDataTrack({
      maxPacketLifeTime: null,
      maxRetransmits: null,
    });

    const connectionOptions = {
      name: options.room,
      tracks: [this.#localDataTrack],
    };

    const handleNewParticipant = (participant: RemoteParticipant) => {
      this.logger.info(`Remote participant joined: ${participant.sid}`);
      this.emit(WebRtcClientEvent.connect);

      const tracks: RemoteAVTrack[] = [];
      participant.tracks.forEach(publication => {
        if (!publication.track) {
          return;
        }

        if (publication.kind !== 'data') {
          tracks.push(publication.track as RemoteAVTrack);
        }
      });

      if (tracks.length) {
        this.#onAVTracksIn(tracks);
      }

      participant.on('trackSubscribed', track => {
        if (track.kind === 'data') {
          this.logger.debug(
            `Setting dataTracksReady to true because local participant subscribed to remote's data track`,
          );
          this.#isDataChannelOpen = true;
        } else {
          this.#onAVTracksIn([track]);
        }
      });

      participant.on('trackUnsubscribed', track => {
        if (track.kind !== 'data') {
          this.#onAVTrackInOff(track);
        } else {
          this.logger.debug(
            `Setting dataTracksReady to false because local participant unsubscribed to remote's data track`,
          );
          this.#isDataChannelOpen = false;
        }
      });
    };

    // TODO: Prevent concurrent connections
    connect(options.token, connectionOptions).then(async room => {
      if (room.name !== this.#roomName) {
        this.logger.info(
          `Connected to room ${room.name} but the most recent room is ${
            this.#roomName
          }. Disconnecting from: ${room.name}`,
        );
        room.disconnect();
        return;
      }

      this.logger.info('Connected to Twilio video room.');
      this.#room = room;

      room.on('trackMessage', (data: string | ArrayBuffer) => {
        if (typeof data !== 'string') {
          data = new TextDecoder('utf-8').decode(data);
        }
        this.emit(WebRtcClientEvent.data, data);
      });

      this.#room.on('disconnected', (room, error) => {
        this.logger.info(`Local participant got disconnected from room ${room.sid}`);
        if (error) {
          this.logger.error(`Unexpectedly disconnected ${error}`);
        }
        this.emit(
          WebRtcClientEvent.disconnect,
          error ? SbPeerDisconnectCode.ERROR : SbPeerDisconnectCode.CLOSED,
        );
        this.#cleanup();
      });

      this.#room.on('participantDisconnected', participant => {
        this.logger.info(`Other participant disconnected: ${participant.sid}`);
        this.logger.debug(
          `Setting dataTracksReady to false because remote participant disconnected from room`,
        );
        this.#isDataChannelOpen = false;
        this.emit(WebRtcClientEvent.disconnect, SbPeerDisconnectCode.CLOSED);
      });

      this.#room.participants.forEach(handleNewParticipant.bind(this));
      this.#room.on('participantConnected', handleNewParticipant.bind(this));
    });
  }

  signal() {
    // Signaling is handled internally by the twilio-video
  }

  disconnect() {
    this.#room?.removeAllListeners();
    this.#room?.disconnect();
    this.#cleanup();
  }

  sendData(data: string): void {
    if (!this.#localDataTrack) {
      this.logger.warn('Attempted to send data without local data track');
      return;
    }
    this.#localDataTrack?.send(data);
  }

  isDataChannelOpen() {
    return this.#isDataChannelOpen;
  }

  isConnected() {
    return this.#room?.state === 'connected' && !!this.#room.participants.size;
  }

  addStream(stream: MediaStream, streamType: SbMediaStreamType): void {
    const tracks: (LocalAudioTrack | LocalVideoTrack)[] = [];

    stream.getTracks().forEach(mediaStreamTrack => {
      // Name works as a content hint for the peer (has to be unique per connection)
      const name = `${mediaStreamTrack.kind}:${streamType}`;
      let track: LocalAVTrack;
      if (mediaStreamTrack.kind === 'audio') {
        track = new LocalAudioTrack(mediaStreamTrack, { name: name });
      } else {
        track = new LocalVideoTrack(mediaStreamTrack, { name: name });
      }
      tracks.push(track);
    });
    this.#streamToAvTrack[stream.id] = tracks;
    this.#publishLocalTracks(tracks);

    this.#streamsOut[streamType] = { stream: stream, twilioTracks: tracks };
  }

  removeStream(stream: MediaStream): void {
    for (const type of Object.keys(SbMediaStreamType) as SbMediaStreamType[]) {
      if (this.#streamsOut[type]?.stream.id === stream.id) {
        this.#streamsOut[type] = null;
      }
    }

    const tracks = this.#streamToAvTrack[stream.id];
    delete this.#streamToAvTrack[stream.id];

    this.#unpublishLocalTracks(tracks);
  }

  getIncomingCameraStream(): MediaStream | null {
    return this.#streamsIn[SbMediaStreamType.camera];
  }

  getOutgoingCameraStream(): MediaStream | null {
    return this.#streamsOut[SbMediaStreamType.camera]?.stream || null;
  }

  getIncomingDisplayStream(): MediaStream | null {
    return this.#streamsIn[SbMediaStreamType.display];
  }

  getOutgoingDisplayStream(): MediaStream | null {
    return this.#streamsOut[SbMediaStreamType.display]?.stream || null;
  }

  setOutgoingAudioEnabled(isEnabled: boolean) {
    const tracks = this.#streamsOut[SbMediaStreamType.camera]?.twilioTracks;
    if (!tracks) {
      return;
    }

    tracks.forEach(t => t.kind === 'audio' && t.enable(isEnabled));
  }

  setOutgoingVideoEnabled(isEnabled: boolean) {
    const tracks = this.#streamsOut[SbMediaStreamType.camera]?.twilioTracks;
    if (!tracks) {
      return;
    }

    tracks.forEach(t => t.kind === 'video' && t.enable(isEnabled));
  }

  static runDiagnostics(token: string, logger: SbLoggerI) {
    const preflightTest = runPreflight(token, {});

    preflightTest.on('completed', report => {
      logger.info('*** PeerConnection diagnostics ***: ok');
      logger.info('Test completed in ' + report.testTiming.duration + ' milliseconds.');
      logger.info(
        'It took ' + report.networkTiming.connect?.duration + ' milliseconds to connect',
      );
      logger.info(
        'It took ' +
          report.networkTiming.media?.duration +
          ' milliseconds to receive media',
      );
    });

    preflightTest.on('failed', (error, report) => {
      logger.info('*** PeerConnection diagnostics ***: error');
      logger.info('preflight error: ' + error.message);
      logger.info('Received partial report: ' + JSON.stringify(report));
    });
  }

  #cleanup() {
    this.removeAllListeners();
    this.#room = null;
    this.#roomName = null;
    this.#isDataChannelOpen = false;

    this.#streamsIn = {
      [SbMediaStreamType.camera]: null,
      [SbMediaStreamType.display]: null,
    };
    this.#streamsOut = {
      [SbMediaStreamType.camera]: null,
      [SbMediaStreamType.display]: null,
    };
  }

  #onAVTrackInOff(twilioTrack: RemoteAudioTrack | RemoteVideoTrack): void {
    const streamType = twilioTrack.name.split(':')[1];
    if (!streamType) {
      return;
    }
    if (!(streamType in SbMediaStreamType)) {
      return;
    }
    const typedStreamType = streamType as SbMediaStreamType;
    const existingStream = this.#streamsIn[typedStreamType];
    if (existingStream) {
      const toRemove: MediaStreamTrack[] = [];
      existingStream.getTracks().forEach(mediaStreamTrack => {
        if (mediaStreamTrack.kind === twilioTrack.mediaStreamTrack.kind) {
          toRemove.push(mediaStreamTrack);
        }
      });
      toRemove.forEach(mediaStreamTrack => {
        existingStream.removeTrack(mediaStreamTrack);
      });
      this.emit(WebRtcClientEvent.streamin, existingStream, typedStreamType);
    }
  }

  #onAVTracksIn(tracks: (RemoteAudioTrack | RemoteVideoTrack)[]): void {
    tracks.forEach(twilioTrack => {
      // Tracks sent by webclients conform to the naming, but the ones from Programmable Voice (phone) do not
      let streamType = twilioTrack.name.split(':')[1];
      if (!streamType) {
        if (twilioTrack.kind === 'audio') {
          streamType = 'camera';
        } else {
          return;
        }
      }
      if (!(streamType in SbMediaStreamType)) {
        return;
      }
      const typedStreamType = streamType as SbMediaStreamType;
      const existingStream = this.#streamsIn[typedStreamType];
      if (existingStream) {
        // Stream of this kind already exist
        // Remove all tracks of this kind from stream and add the new one
        const toRemove: MediaStreamTrack[] = [];
        existingStream.getTracks().forEach(mediaStreamTrack => {
          if (mediaStreamTrack.kind === twilioTrack.mediaStreamTrack.kind) {
            toRemove.push(mediaStreamTrack);
          }
        });
        toRemove.forEach(mediaStreamTrack => {
          existingStream.removeTrack(mediaStreamTrack);
        });
        existingStream.addTrack(twilioTrack.mediaStreamTrack);
        // Clone to change stream id - redux relies on that for updates
        this.#streamsIn[typedStreamType] = existingStream.clone();
        this.emit(
          WebRtcClientEvent.streamin,
          this.#streamsIn[typedStreamType],
          typedStreamType,
        );
      } else {
        // Stream of this kind does not yet exist
        // Create a new one and publish an event
        const newStream = new MediaStream([twilioTrack.mediaStreamTrack]);
        this.#streamsIn[typedStreamType] = newStream;
        this.emit(WebRtcClientEvent.streamin, newStream, typedStreamType);
      }
    });
  }

  #publishLocalTracks(tracks: LocalAVTrack[]) {
    // There unpublishing and then publishing a track may cause a race condition.
    // This is a workaround
    // https://github.com/twilio/twilio-video.js/issues/1227
    if (!this.#room) {
      return;
    }

    const rawPublishTracks = () => {
      return (this.#room as Room)?.localParticipant?.publishTracks(tracks);
    };

    if (this.#asyncTrackOperation) {
      this.#asyncTrackOperation = this.#asyncTrackOperation.finally(rawPublishTracks);
    } else {
      this.#asyncTrackOperation = rawPublishTracks();
    }

    this.#asyncTrackOperation.catch(err => {
      if (err && err.code === 53304) {
        return new Promise<void>((resolve, reject) => {
          setTimeout(async () => {
            this.logger.warn('Attempt to workaround "Track name is duplicated" error');
            try {
              await rawPublishTracks();
              resolve();
            } catch (e) {
              reject(e);
            }
          }, 1000);
        });
      } else {
        throw err;
      }
    });
  }

  #unpublishLocalTracks(tracks: LocalAVTrack[]) {
    const rawUnPublishTracks = () => {
      return (this.#room as Room)?.localParticipant?.unpublishTracks(tracks);
    };

    if (this.#asyncTrackOperation) {
      this.#asyncTrackOperation = this.#asyncTrackOperation.finally(rawUnPublishTracks);
    } else {
      rawUnPublishTracks();
    }
  }
}

export { LocalVideoTrack };
